import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import type { Product } from '@/services/products'
import { listProductDisplayPrice } from '@/storefront/lib/listPrice'
import { useStorefrontBase } from '@/storefront/hooks/useStorefrontBase'
import { useOptionalReferenceStore } from '@/storefront/context/ReferenceStoreContext'
import { useReferenceProducts } from '@/storefront/hooks/useReferenceCatalog'
import { cn } from '@/lib/utils'
import type { QuickViewLayoutMode } from '@/lib/blocks/quickViewFromBlocks'
import { useTheme } from '@/storefront/theme/ThemeContext'
import { ProductQuickViewDrawer } from '@/storefront/components/ProductQuickViewDrawer'

export type QuickViewContextValue = {
  enabled: boolean
  open: (product: Product) => void
  close: () => void
}

const QuickViewContext = createContext<QuickViewContextValue | null>(null)

export function useQuickView(): QuickViewContextValue | null {
  return useContext(QuickViewContext)
}

function QuickViewModalSimple({
  product,
  isLoading,
  onClose,
}: {
  product: Product | null
  isLoading: boolean
  onClose: () => void
}) {
  const base = useStorefrontBase()
  const img = product?.cover_image || product?.image || product?.images?.[0]
  const price = product ? listProductDisplayPrice(product) : '—'
  const to = product
    ? `${base}/product/${encodeURIComponent(product.slug || product.id)}`
    : `${base}/shop`

  return (
    <div
      className="fixed inset-0 z-dv-sticky-bar flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="Aperçu produit"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-card/95 text-foreground shadow-md ring-1 ring-border/80 hover:bg-muted"
          aria-label="Fermer l’aperçu"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        <div className="aspect-[4/3] bg-muted">
          {isLoading || !product ? (
            <div className="flex h-full animate-pulse flex-col gap-3 p-4 motion-reduce:animate-none">
              <div className="h-full w-full rounded-lg bg-muted/90" />
            </div>
          ) : img ? (
            <img src={img} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Pas d’image</div>
          )}
        </div>
        <div className="space-y-3 p-5">
          {isLoading || !product ? (
            <>
              <div className="h-5 w-[75%] animate-pulse rounded bg-muted motion-reduce:animate-none" />
              <div className="h-4 w-24 animate-pulse rounded bg-muted motion-reduce:animate-none" />
            </>
          ) : (
            <>
              <h2 className="pr-10 text-lg font-bold tracking-tight text-foreground">{product.name}</h2>
              <p className="text-base font-semibold tabular-nums text-foreground">{price}</p>
            </>
          )}
          <Link
            to={to}
            className="inline-flex w-full min-h-11 items-center justify-center rounded-xl bg-ref-primary px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 disabled:pointer-events-none disabled:opacity-40"
            onClick={onClose}
            aria-label={product ? 'Voir la fiche produit complète' : 'Continuer vers la boutique'}
          >
            {product ? 'Voir la fiche complète' : 'Continuer'}
          </Link>
        </div>
      </div>
    </div>
  )
}

function QuickViewModal({
  productId,
  product,
  isLoading,
  layout,
  onClose,
}: {
  productId: string
  product: Product | null
  isLoading: boolean
  layout: QuickViewLayoutMode
  onClose: () => void
}) {
  if (layout === 'advanced') {
    return (
      <ProductQuickViewDrawer
        open
        productId={productId}
        product={product}
        isLoading={isLoading}
        onClose={onClose}
      />
    )
  }
  return <QuickViewModalSimple product={product} isLoading={isLoading} onClose={onClose} />
}

export function QuickViewProvider({
  enabled,
  layout = 'simple',
  children,
}: {
  enabled: boolean
  layout?: QuickViewLayoutMode
  children: ReactNode
}) {
  const theme = useTheme()
  const refStore = useOptionalReferenceStore()
  const storeId = refStore?.store?.id
  const [quickViewProductId, setQuickViewProductId] = useState<string | null>(null)
  /** Grille / carte : affichage immédiat pendant le fetch PDP (évite skeleton vide si réseau lent). */
  const [quickViewSeedProduct, setQuickViewSeedProduct] = useState<Product | null>(null)

  const productsQ = useReferenceProducts(
    quickViewProductId && storeId ? { storeId, ids: [quickViewProductId] } : undefined
  )

  const resolvedProduct = useMemo(() => {
    if (!quickViewProductId) return null
    const fromQuery = productsQ.data?.find((p) => p.id === quickViewProductId)
    if (fromQuery) return fromQuery
    if (quickViewSeedProduct?.id === quickViewProductId) return quickViewSeedProduct
    return null
  }, [quickViewProductId, productsQ.data, quickViewSeedProduct])

  const isLoading =
    !!quickViewProductId &&
    !resolvedProduct &&
    (productsQ.isPending || productsQ.isFetching)

  const open = useCallback(
    (p: Product) => {
      if (!enabled) return
      setQuickViewProductId(p.id)
      setQuickViewSeedProduct(p)
    },
    [enabled]
  )

  const close = useCallback(() => {
    setQuickViewProductId(null)
    setQuickViewSeedProduct(null)
  }, [])

  const value = useMemo(
    () => ({
      enabled,
      open,
      close,
    }),
    [enabled, open, close]
  )

  return (
    <QuickViewContext.Provider value={value}>
      {children}
      {enabled && quickViewProductId ? (
        <div
          className={cn('ref-storefront', theme.rootClassName)}
          style={theme.cssVars as CSSProperties}
        >
          <QuickViewModal
            productId={quickViewProductId}
            product={resolvedProduct}
            isLoading={isLoading}
            layout={layout}
            onClose={close}
          />
        </div>
      ) : null}
    </QuickViewContext.Provider>
  )
}
